/**
 * One project's Shared Docs session, without a layout attached.
 *
 * Lifted out of `CollabDocsShell`, which had this welded to the docs route's
 * tree-and-breadcrumb chrome. A second surface now needs the session and none
 * of the chrome: a feedback request renders live previews of the mockups being
 * voted on, and resolving a document id to the extension editor that can paint
 * it means reading this project's document index.
 *
 * Everything subtle here was learned by the docs route and is preserved
 * verbatim rather than re-derived -- see the comments on `callbacks` and on the
 * host's memo dependencies, both of which record real regressions.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { CollabDocsSession } from '@nimbalyst/collab-bundle/docs-ui';
import { getPageMarksSource, setPageMarksSource } from '@nimbalyst/collab-bundle/pages';

import { clearWebCanvasCardScope, setWebCanvasCardScope } from '../canvas/webCanvasCardScope';
import type { TeamSummary } from '../api';
import type { TeamJwt, TeamSession } from '../auth/teamJwt';
import { getProjectRouteId } from '../projectSelection';
import type { WebCollabDocumentsDataSource } from './WebCollabDocumentsDataSource';
import { WebCollabHost, createWebCollabLifecycle } from './WebCollabHost';

/** Same derivation the docs shell uses; a project is whatever the team lists. */
type TeamProject = TeamSummary['projects'][number];

export interface ProjectDocsScopeOptions {
  team: TeamSummary;
  /**
   * Null when the caller could not name a project, which is a real answer
   * rather than a missing one: a legacy resource ref with no project id, in an
   * org with several projects, must not be routed through a guessed room. No
   * host is built and no socket opens; the caller degrades.
   */
  project: TeamProject | null;
  teamSession: TeamSession;
  getTeamJwt: (options?: { forceRefresh?: boolean }) => Promise<TeamJwt>;
  serverUrl: string;
  navigate: (path: string) => void;
  createDataSource?: (
    options: ConstructorParameters<typeof WebCollabDocumentsDataSource>[0],
  ) => WebCollabDocumentsDataSource;
  onError?: (cause: unknown) => void;
}

export interface ProjectDocsScope {
  session: CollabDocsSession | null;
  error: Error | null;
}

/**
 * Session and failure state, tagged with the host they belong to.
 *
 * They are stored together and tagged because both are per-project and both
 * have to be dropped at the same instant the host changes. Keeping them as two
 * untagged `useState` values let a project switch render the incoming
 * project's heading above the outgoing project's tree, and let a failure from
 * the project the user left sit permanently over the project they went to.
 */
interface DocsSessionState {
  host: WebCollabHost | null;
  session: CollabDocsSession | null;
  error: Error | null;
}

function docsError(cause: unknown): Error {
  return cause instanceof Error ? cause : new Error(String(cause));
}

export function useProjectDocsScope({
  team,
  project,
  teamSession,
  getTeamJwt,
  serverUrl,
  navigate,
  createDataSource,
  onError,
}: ProjectDocsScopeOptions): ProjectDocsScope {
  const [state, setState] = useState<DocsSessionState>({
    host: null,
    session: null,
    error: null,
  });
  const projectId = project ? getProjectRouteId(project) : null;

  /*
   * Everything the host calls out to, kept current without being part of the
   * host's identity. `useNavigate` returns a *new* function on every location
   * change, so a host memoized over the callbacks was rebuilt every time a user
   * opened a document from the tree: the index socket closed, the session was
   * disposed, and the tree fell back to its loading skeleton and re-synced.
   */
  const callbacks = useRef({
    team, project, teamSession, getTeamJwt, navigate, createDataSource, onError,
  });
  callbacks.current = {
    team, project, teamSession, getTeamJwt, navigate, createDataSource, onError,
  };

  // A host *is* one project's shared-docs scope. Only the values that name that
  // scope may rebuild it — never a callback identity, and never a route change.
  const host = useMemo(() => {
    if (projectId === null) return null;
    // `self` is what every late callback reports against. The host's own async
    // work can fail after the user has already switched projects, and an
    // untagged report would paint the outgoing project's failure over the
    // incoming project.
    const self: WebCollabHost = new WebCollabHost({
      team: callbacks.current.team,
      projectId,
      teamSession: callbacks.current.teamSession,
      getTeamJwt: (options) => callbacks.current.getTeamJwt(options),
      serverUrl,
      navigate: (path) => callbacks.current.navigate(path),
      createDataSource: callbacks.current.createDataSource,
      reportError: (cause) => {
        callbacks.current.onError?.(cause);
        setState(prev => (prev.host === self ? { ...prev, error: docsError(cause) } : prev));
      },
    });
    return self;
  }, [projectId, serverUrl, teamSession.orgId, teamSession.memberId]);

  // Adjusting state during render, not in an effect: an effect runs after the
  // browser has already painted, so a project switch would show one frame of
  // the previous project's disposed session.
  if (state.host !== host) setState({ host, session: null, error: null });
  const current = state.host === host ? state : null;

  useEffect(() => {
    if (!host) return;
    // A canvas card names an org and a document id but never a project, and the
    // board paints in its own React root where no provider reaches it. This is
    // the one place that knows when a project scope starts and stops, so it is
    // what publishes the scope those cards resolve against.
    const cardScope = host.canvasCardScope();
    setWebCanvasCardScope(cardScope);
    // A marks view placed in a page lists the team's marks the same way.
    const marksSource = host.pageMarksSource();
    setPageMarksSource(marksSource);
    // The host identity is the guard, not a `mounted` flag. `dispose()` clears
    // the session synchronously through `onSessionChanged(null)`, and a flag
    // set before disposal swallowed exactly that call.
    const lifecycle = createWebCollabLifecycle(host, {
      onSessionChanged: (nextSession) => {
        setState(prev => (prev.host === host ? { ...prev, session: nextSession } : prev));
      },
      onError: (cause) => {
        callbacks.current.onError?.(cause);
        setState(prev => (prev.host === host ? { ...prev, error: docsError(cause) } : prev));
      },
    });
    lifecycle.start();
    return () => {
      clearWebCanvasCardScope(cardScope);
      if (getPageMarksSource() === marksSource) setPageMarksSource(null);
      lifecycle.dispose();
    };
  }, [host]);

  return { session: current?.session ?? null, error: current?.error ?? null };
}
